import { computed, Injectable, signal } from '@angular/core';
import { CurrentUser, TenantFeatureSource } from './auth.model';

const TENANT_FEATURE_USER_KEYS = [
	'tenantFeatures',
	'features',
	'featureFlags',
	'tenantFeatureCodes',
	'featureCodes',
] as const;

@Injectable({ providedIn: 'root' })
export class AuthStore {
	private readonly _user = signal<CurrentUser | null>(null);
	private readonly _permissions = signal<Set<string>>(new Set());
	private readonly _tenantFeatures = signal<TenantFeatureSource[] | null>(null);
	private readonly _tenantFeatureCodes = signal<Set<string> | null>(null);

	readonly user = this._user.asReadonly();
	readonly isAuthenticated = computed(() => this._user() !== null);
	// Mirrors the backend auditAccessGuard: platform admin (owner/super_admin) or
	// tenant admin. Used to gate admin-only views (e.g. audit / state-history).
	readonly isAdmin = computed(() => {
		const u = this._user();
		return (
			u?.globalRole === 'owner' || u?.globalRole === 'super_admin' || u?.tenantRole === 'admin'
		);
	});
	readonly tenantFeatures = this._tenantFeatures.asReadonly();
	readonly enabledTenantFeatureCodes = computed(() => {
		const tenantFeatures = this._tenantFeatureCodes();
		return tenantFeatures ? Array.from(tenantFeatures) : null;
	});

	setUser(user: CurrentUser): void {
		const tenantFeatures = this.resolveTenantFeatureSources(user);

		this._user.set(user);
		this._permissions.set(new Set(user.permissions));
		this._tenantFeatures.set(tenantFeatures);
		this._tenantFeatureCodes.set(this.resolveTenantFeatureCodes(tenantFeatures));
	}

	clearUser(): void {
		this._user.set(null);
		this._permissions.set(new Set());
		this._tenantFeatures.set(null);
		this._tenantFeatureCodes.set(null);
	}

	setPermissions(codes: string[]): void {
		this._permissions.set(new Set(codes));
	}

	can(code: string): boolean {
		const permissions = this._permissions();
		return permissions.has('*') || permissions.has(code);
	}

	canAny(codes: readonly string[]): boolean {
		return codes.some((code) => this.can(code));
	}

	canAll(codes: readonly string[]): boolean {
		return codes.every((code) => this.can(code));
	}

	hasFeature(code: string): boolean {
		const user = this._user();
		if (!user) return false;
		if (!user.tenantId) return true;

		const tenantFeatures = this._tenantFeatureCodes();
		if (tenantFeatures === null) return true;

		return tenantFeatures.has('*') || tenantFeatures.has(this.normalizeTenantFeatureCode(code));
	}

	hasAnyFeature(codes: readonly string[] | undefined): boolean {
		if (!codes?.length) return true;
		return codes.some((code) => this.hasFeature(code));
	}

	hasTenantFeature(code: string): boolean {
		return this.hasFeature(code);
	}

	hasAnyTenantFeature(codes: readonly string[] | undefined): boolean {
		return this.hasAnyFeature(codes);
	}

	private resolveTenantFeatureSources(user: CurrentUser): TenantFeatureSource[] | null {
		for (const key of TENANT_FEATURE_USER_KEYS) {
			const source = user[key];
			if (!Array.isArray(source)) continue;

			return source;
		}

		return null;
	}

	private resolveTenantFeatureCodes(source: TenantFeatureSource[] | null): Set<string> | null {
		if (!source) return null;

		return new Set(
			source
				.filter((value) => this.isTenantFeatureEnabled(value))
				.map((value) => this.extractTenantFeatureCode(value))
				.filter((code): code is string => !!code)
				.map((code) => this.normalizeTenantFeatureCode(code)),
		);
	}

	private isTenantFeatureEnabled(value: TenantFeatureSource): boolean {
		if (typeof value === 'string') return true;

		return (value.isEnabled ?? value.enabled ?? value.active ?? value.defaultEnabled) !== false;
	}

	private extractTenantFeatureCode(value: TenantFeatureSource): string | null {
		if (typeof value === 'string') return value;

		return (
			this.firstString(
				value.code,
				value.featureCode,
				value.featureFlagCode,
				value.featureFlagId,
			) ??
			this.firstString(
				value.featureFlag?.code,
				value.featureFlag?.featureCode,
				value.featureFlag?.featureFlagCode,
				value.featureFlag?.featureFlagId,
			)
		);
	}

	private firstString(...values: Array<string | null | undefined>): string | null {
		return values.find((value) => !!value?.trim())?.trim() ?? null;
	}

	private normalizeTenantFeatureCode(code: string): string {
		return code
			.trim()
			.toLowerCase()
			.replace(/[\s-]+/g, '_');
	}
}
